import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../shared/auth/AuthContext';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';
import { Button, Card, Badge, Input, Modal, Drawer, LoadingSkeleton, Tabs } from '@nexora/design-system';
import { EMPTY, formatCurrency, formatDocument, formatDate } from '../../shared/utils/format';
import { ContactsDirectory } from '../crm/ContactsDirectory';
import { useInteractions } from '../crm/useInteractions';

/* ── Types ── */

interface Address {
  street?: string;
  number?: string;
  complement?: string;
  district?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

interface Party {
  id: string;
  name: string;
  fantasyName?: string;
  documentNumber?: string;
  documentType?: 'CPF' | 'CNPJ';
  stateRegistration?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  website?: string;
  address?: Address;
  tags?: string[];
  notes?: string;
  creditLimit?: number;
  type: string;
  status: string;
  createdAt: string;
}

interface PartyList {
  data: Party[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface CreatePartyPayload {
  type: string;
  name: string;
  documentType?: 'CPF' | 'CNPJ';
  documentNumber?: string;
  email?: string;
  phone?: string;
}

/* ── Helpers ── */

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

const typeLabel: Record<string, string> = {
  CUSTOMER: 'Cliente',
  SUPPLIER: 'Fornecedor',
  TRANSPORTER: 'Transportadora',
  COMMERCIAL_AGENT: 'Representante',
  INSURER: 'Seguradora',
  OTHER: 'Outro',
};

const statusLabel: Record<string, string> = {
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  BLOCKED: 'Bloqueado',
};

const statusVariant: Record<string, 'success' | 'error' | 'neutral'> = {
  ACTIVE: 'success',
  INACTIVE: 'neutral',
  BLOCKED: 'error',
};

/* ── Component ── */

function TenantInteractionsList({ onSelect }: { onSelect: (id: string) => void }): JSX.Element {
  const { data, isLoading, error } = useInteractions({ limit: 50 });
  const rows = data?.data ?? [];

  if (isLoading) return <LoadingSkeleton count={4} height="64px" />;
  if (error) {
    return <p className="font-body-md text-body-md text-error">{(error as Error).message}</p>;
  }
  if (rows.length === 0) {
    return (
      <Card>
        <p className="mb-xs mt-0 font-headline-sm text-headline-sm">Nenhuma interação</p>
        <p className="m-0 font-body-md text-body-md text-on-surface-variant">
          Registre pela ficha do cliente — o histórico aparece aqui.
        </p>
      </Card>
    );
  }
  return (
    <Card padding="0">
      <ul className="m-0 flex flex-col p-0" style={{ listStyle: 'none' }}>
        {rows.map((interaction) => (
          <li key={interaction.id} className="border-b border-outline-variant last:border-b-0">
            <button
              type="button"
              onClick={() => onSelect(interaction.id)}
              className="block w-full p-md text-left hover:bg-surface-container-low"
            >
              <div className="mb-xs flex flex-wrap items-center gap-sm">
                <span className="rounded-sm bg-primary-container px-xs py-[2px] font-label-sm text-label-sm font-semibold text-on-primary-container">
                  {interaction.type}
                </span>
                <span className="font-label-sm text-label-sm text-on-surface-variant">
                  {interaction.customerName ?? interaction.customerId.slice(0, 8)}
                  {interaction.contactName ? ` · ${interaction.contactName}` : ''}
                </span>
                <span className="font-label-sm text-label-sm text-on-surface-variant">
                  {formatDate(interaction.occurredAt)}
                </span>
              </div>
              <p className="m-0 line-clamp-2 font-body-md text-body-md text-on-surface">
                {interaction.notes}
              </p>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function InteractionDetail({
  interactionId,
  onBack,
}: {
  interactionId: string | null;
  onBack: () => void;
}): JSX.Element {
  const { data, isLoading, error } = useInteractions({ limit: 100 });
  const interaction = data?.data.find((i) => i.id === interactionId) ?? null;

  if (isLoading) return <LoadingSkeleton count={3} height="64px" />;
  if (error) {
    return <p className="font-body-md text-body-md text-error">{(error as Error).message}</p>;
  }
  if (!interaction) {
    return (
      <Card>
        <p className="mb-xs mt-0 font-headline-sm text-headline-sm">Selecione uma interação</p>
        <p className="m-0 font-body-md text-body-md text-on-surface-variant">
          Escolha um registro na aba Contatos e Interações para ver o detalhe.
        </p>
      </Card>
    );
  }
  return (
    <Card>
      <Button variant="outline" size="sm" onClick={onBack}>
        ← Voltar
      </Button>
      <h3 className="mb-sm mt-md font-headline-sm text-headline-sm text-on-surface">
        {interaction.type} · {formatDate(interaction.occurredAt)}
      </h3>
      <p className="mb-sm font-body-md text-body-md text-on-surface">{interaction.notes}</p>
      <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
        Cliente: {interaction.customerName ?? interaction.customerId.slice(0, 8)}
        {interaction.contactName ? ` · Contato: ${interaction.contactName}` : ''}
        {interaction.participants ? ` · Com: ${interaction.participants}` : ''}
      </p>
    </Card>
  );
}

export function PartiesPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [tab, setTab] = useState<'diretorio' | 'interacoes' | 'detalhe'>('diretorio');
  const [selectedInteractionId, setSelectedInteractionId] = useState<string | null>(null);
  const [form, setForm] = useState<CreatePartyPayload>({
    type: 'CUSTOMER',
    name: '',
    documentType: 'CNPJ',
    documentNumber: '',
    email: '',
    phone: '',
  });
  const pageSize = 20;

  const { data, isLoading, error } = useQuery<PartyList>({
    queryKey: [...queryKeys.masterData.parties(tenantId), search, typeFilter, statusFilter, page],
    queryFn: async () =>
      apiClient.get<PartyList>('/parties', {
        params: {
          page: String(page + 1),
          limit: String(pageSize),
          ...(search ? { search } : {}),
          /* tipo e status filtram no servidor: filtrar aqui veria 20 de 400
             e o rodape continuaria contando 400. */
          ...(typeFilter ? { type: typeFilter } : {}),
          ...(statusFilter ? { status: statusFilter } : {}),
        },
      }),
    enabled: !!tenantId,
  });

  const createMutation = useMutation({
    mutationFn: async (payload: CreatePartyPayload) => apiClient.post<Party>('/parties', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.masterData.parties(tenantId) });
      setShowModal(false);
      setForm({ type: 'CUSTOMER', name: '', documentType: 'CNPJ', documentNumber: '', email: '', phone: '' });
    },
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const selected = rows.find((p) => p.id === selectedId) ?? null;
  const hasFilter = !!search || !!typeFilter || !!statusFilter;
  const firstRow = total === 0 ? 0 : page * pageSize + 1;
  const lastRow = Math.min(total, page * pageSize + rows.length);

  const clearFilters = (): void => {
    setSearch('');
    setTypeFilter('');
    setStatusFilter('');
    setPage(0);
  };

  const copy = (value: string, field: string): void => {
    void navigator.clipboard?.writeText(value);
    setCopied(field);
    window.setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="parties-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-lg gap-md">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Contatos</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Diretório global de pessoas e interações.
          </p>
        </div>
        <Button variant="primary" onClick={() => setShowModal(true)}>
          Nova pessoa
        </Button>
      </div>

      <div className="mb-lg">
        <Tabs
          tabs={[
            { key: 'diretorio', label: 'Diretório Global' },
            { key: 'interacoes', label: 'Contatos e Interações' },
            { key: 'detalhe', label: 'Detalhe da Interação' },
          ]}
          value={tab}
          onChange={(key) => setTab(key as typeof tab)}
          aria-label="Abas de contatos"
        />
      </div>

      {tab === 'interacoes' ? (
        <TenantInteractionsList
          onSelect={(id) => {
            setSelectedInteractionId(id);
            setTab('detalhe');
          }}
        />
      ) : null}

      {tab === 'detalhe' ? (
        <InteractionDetail
          interactionId={selectedInteractionId}
          onBack={() => setTab('interacoes')}
        />
      ) : null}

      {tab === 'diretorio' ? (
      <>
      <ContactsDirectory />

      <h2 className="mb-md mt-xl font-headline-sm text-headline-sm text-on-surface">
        Parceiros (master data)
      </h2>

      {/* ── Barra de filtros ── */}
      <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-md mb-lg shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-md">
          <div className="flex flex-col gap-xs">
            <label className="font-label-md text-label-md text-on-surface-variant uppercase" htmlFor="party-search">
              Busca
            </label>
            <input
              id="party-search"
              className="parties-control"
              placeholder="Nome ou documento..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
            />
          </div>
          <div className="flex flex-col gap-xs">
            <label className="font-label-md text-label-md text-on-surface-variant uppercase" htmlFor="party-type">
              Tipo
            </label>
            <select
              id="party-type"
              className="parties-control"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(0);
              }}
            >
              <option value="">Todos os tipos</option>
              {Object.entries(typeLabel).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-xs">
            <label className="font-label-md text-label-md text-on-surface-variant uppercase" htmlFor="party-status">
              Status
            </label>
            <select
              id="party-status"
              className="parties-control"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <option value="">Todos os status</option>
              {Object.entries(statusLabel).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-md flex justify-between items-center border-t border-outline-variant pt-md">
          <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
            {isLoading ? 'Carregando...' : `${total} ${total === 1 ? 'contato' : 'contatos'}`}
          </span>
          {hasFilter && (
            <button type="button" className="parties-clear" onClick={clearFilters}>
              Limpar filtros
            </button>
          )}
        </div>
      </div>

      {/* ── Tabela densa ── */}
      <Card padding="0">
        {isLoading && (
          <div style={{ padding: '1rem' }}>
            <LoadingSkeleton count={6} height="40px" />
          </div>
        )}

        {!isLoading && error && (
          <div className="parties-empty">
            <p>{(error as Error).message}</p>
          </div>
        )}

        {!isLoading && !error && rows.length === 0 && (
          <div className="parties-empty">
            <p>Nenhuma pessoa encontrada.</p>
          </div>
        )}

        {!isLoading && !error && rows.length > 0 && (
          <>
            <div className="parties-table">
              <table>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Documento</th>
                    <th>Tipo</th>
                    <th>Contato</th>
                    <th>Status</th>
                    <th>Criado em</th>
                    <th style={{ textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr
                      key={p.id}
                      className={p.id === selectedId ? 'is-selected' : undefined}
                      onClick={() => setSelectedId(p.id === selectedId ? null : p.id)}
                    >
                      <td>
                        <span className="parties-name">{p.name}</span>
                        {p.fantasyName && <span className="parties-sub">{p.fantasyName}</span>}
                      </td>
                      <td className="font-label-md text-label-md">
                        {formatDocument(p.documentNumber, p.documentType)}
                      </td>
                      <td>{typeLabel[p.type] ?? p.type}</td>
                      <td>
                        <span className="parties-sub">{p.email ?? EMPTY}</span>
                        <span className="parties-sub">{p.phone ?? p.mobile ?? EMPTY}</span>
                      </td>
                      <td>
                        <Badge variant={statusVariant[p.status] ?? 'neutral'} size="sm">
                          {statusLabel[p.status] ?? p.status}
                        </Badge>
                      </td>
                      <td>{formatDate(p.createdAt)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {/* Ações que existem de fato: e-mail e telefone cadastrados.
                            Sem contato cadastrado nao ha acao — e o traco diz isso. */}
                        <div className="parties-actions">
                          {p.email ? (
                            <a href={`mailto:${p.email}`} title={`Enviar e-mail para ${p.email}`}>
                              <span className="material-symbols-outlined" aria-hidden="true">
                                mail
                              </span>
                              <span className="sr-only">Enviar e-mail</span>
                            </a>
                          ) : null}
                          {p.phone ?? p.mobile ? (
                            <a href={`tel:${p.phone ?? p.mobile}`} title={`Ligar para ${p.phone ?? p.mobile}`}>
                              <span className="material-symbols-outlined" aria-hidden="true">
                                call
                              </span>
                              <span className="sr-only">Ligar</span>
                            </a>
                          ) : null}
                          {!p.email && !p.phone && !p.mobile && (
                            <span className="parties-sub">{EMPTY}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="parties-footer">
              <span className="font-body-sm text-body-sm text-on-surface-variant tabular-nums">
                Mostrando {firstRow} a {lastRow} de {total}
              </span>
              <div className="flex gap-xs">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={lastRow >= total}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* ── Drawer contextual ── */}
      <Drawer
        open={!!selected}
        onClose={() => setSelectedId(null)}
        title={selected?.name ?? ''}
        description={
          selected
            ? `${typeLabel[selected.type] ?? selected.type}${selected.fantasyName ? ` · ${selected.fantasyName}` : ''}`
            : undefined
        }
      >
        {selected && (
          <>
            <div className="flex items-center gap-md mb-lg">
              <div className="parties-avatar">{initials(selected.name)}</div>
              <div>
                <Badge variant={statusVariant[selected.status] ?? 'neutral'} size="sm">
                  {statusLabel[selected.status] ?? selected.status}
                </Badge>
                <p className="font-label-md text-label-md text-on-surface-variant mt-xs mb-0">
                  {formatDocument(selected.documentNumber, selected.documentType)}
                </p>
              </div>
            </div>

            <fieldset className="parties-fieldset">
              <legend>Contato</legend>
              {([
                ['mail', selected.email, 'email'],
                ['call', selected.phone, 'phone'],
                ['smartphone', selected.mobile, 'mobile'],
                ['language', selected.website, 'website'],
              ] as const).map(([icon, value, field]) =>
                value ? (
                  <div className="parties-contact-row" key={field}>
                    <span className="font-body-sm text-body-sm text-on-surface-variant">
                      <span className="material-symbols-outlined" aria-hidden="true">
                        {icon}
                      </span>
                      {value}
                    </span>
                    <button type="button" onClick={() => copy(value, field)}>
                      {copied === field ? 'Copiado' : 'Copiar'}
                    </button>
                  </div>
                ) : null,
              )}
              {!selected.email && !selected.phone && !selected.mobile && !selected.website && (
                <p className="font-body-sm text-body-sm text-on-surface-variant m-0">
                  Nenhum contato cadastrado.
                </p>
              )}
            </fieldset>

            {selected.address && (
              <fieldset className="parties-fieldset">
                <legend>Endereço</legend>
                <p className="font-body-sm text-body-sm text-on-surface m-0">
                  {[
                    [selected.address.street, selected.address.number].filter(Boolean).join(', '),
                    selected.address.complement,
                    selected.address.district,
                    [selected.address.city, selected.address.state].filter(Boolean).join(' - '),
                    selected.address.zipCode,
                  ]
                    .filter(Boolean)
                    .join(' · ') || EMPTY}
                </p>
              </fieldset>
            )}

            <fieldset className="parties-fieldset">
              <legend>Cadastro</legend>
              <dl className="grid grid-cols-2 gap-md m-0">
                {[
                  ['Inscrição estadual', selected.stateRegistration ?? EMPTY],
                  ['Limite de crédito', formatCurrency(selected.creditLimit)],
                  ['Criado em', formatDate(selected.createdAt)],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="font-label-md text-label-md text-on-surface-variant uppercase">{label}</dt>
                    <dd className="font-body-md text-body-md text-on-surface m-0">{value}</dd>
                  </div>
                ))}
              </dl>
            </fieldset>

            {selected.tags && selected.tags.length > 0 && (
              <fieldset className="parties-fieldset">
                <legend>Marcadores</legend>
                <div className="flex flex-wrap gap-xs">
                  {selected.tags.map((tag) => (
                    <Badge key={tag} variant="info" size="sm">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </fieldset>
            )}

            {selected.notes && (
              <fieldset className="parties-fieldset">
                <legend>Observações</legend>
                <p className="font-body-sm text-body-sm text-on-surface m-0">{selected.notes}</p>
              </fieldset>
            )}
          </>
        )}
      </Drawer>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Nova pessoa">
        <div className="modal-form">
          <div className="form-group">
            <label htmlFor="new-party-type">Tipo</label>
            <select
              id="new-party-type"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="parties-control"
            >
              {Object.entries(typeLabel).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="new-party-doc-type">Tipo de documento</label>
            <select
              id="new-party-doc-type"
              value={form.documentType}
              onChange={(e) => setForm({ ...form, documentType: e.target.value as 'CPF' | 'CNPJ' })}
              className="parties-control"
            >
              <option value="CNPJ">CNPJ</option>
              <option value="CPF">CPF</option>
            </select>
          </div>
          <Input
            label="Nome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Nome completo / Razão social"
          />
          <Input
            label="Documento"
            value={form.documentNumber ?? ''}
            onChange={(e) => setForm({ ...form, documentNumber: e.target.value })}
            placeholder="Apenas números"
          />
          <Input
            label="E-mail"
            type="email"
            value={form.email ?? ''}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <Input
            label="Telefone"
            value={form.phone ?? ''}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <div className="modal-actions">
            <Button variant="ghost" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={() => createMutation.mutate(form)}
              disabled={!form.name || createMutation.isPending}
            >
              {createMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
          {createMutation.isError && (
            <p className="error-message">{(createMutation.error as Error).message}</p>
          )}
        </div>
      </Modal>
      </>
      ) : null}

      <style>{`
        .parties-page { padding: 1.5rem; }

        .parties-control {
          width: 100%;
          padding: 0.5rem;
          background: #f7f9fb;
          border: 1px solid #c4c6cf;
          border-radius: 2px;
          font-family: inherit;
          font-size: 0.875rem;
          color: #191c1e;
        }
        .parties-control:focus {
          outline: none;
          border-color: #002045;
          box-shadow: 0 0 0 1px #002045;
        }
        .parties-clear {
          background: none;
          border: none;
          padding: 0;
          color: #002045;
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 12px;
          cursor: pointer;
        }
        .parties-clear:hover { text-decoration: underline; }

        .parties-table { overflow-x: auto; }
        .parties-table table { width: 100%; border-collapse: collapse; }
        .parties-table thead th {
          position: sticky;
          top: 0;
          z-index: 1;
          background: #f2f4f6;
          border-bottom: 1px solid #c4c6cf;
          padding: 0.5rem 0.75rem;
          text-align: left;
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 10px;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #43474e;
          white-space: nowrap;
        }
        .parties-table tbody td {
          padding: 0.625rem 0.75rem;
          border-bottom: 1px solid #eceef0;
          font-size: 0.875rem;
          color: #191c1e;
          vertical-align: middle;
        }
        .parties-table tbody tr { cursor: pointer; }
        .parties-table tbody tr:hover { background: #f7f9fb; }
        .parties-table tbody tr.is-selected { background: #d0e1fb; }
        .parties-name { display: block; font-weight: 500; }
        .parties-sub { display: block; font-size: 12px; color: #43474e; }

        /* Acao revelada no hover da linha, como na referencia. A regra de
           focus-within vai junto: opacidade 0 esconderia o alvo tambem de quem
           navega por teclado, e ai a acao deixaria de existir para essa pessoa. */
        .parties-actions {
          display: flex;
          justify-content: flex-end;
          gap: 0.25rem;
          opacity: 0;
          transition: opacity 120ms ease-in-out;
        }
        .parties-table tbody tr:hover .parties-actions,
        .parties-table tbody tr.is-selected .parties-actions,
        .parties-actions:focus-within { opacity: 1; }
        @media (hover: none) { .parties-actions { opacity: 1; } }
        .parties-actions a {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 2px;
          color: #43474e;
          background: #eceef0;
          text-decoration: none;
        }
        .parties-actions a:hover { background: #e0e3e5; color: #002045; }
        .parties-actions .material-symbols-outlined { font-size: 18px; }

        .parties-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 0.75rem 1rem;
          border-top: 1px solid #c4c6cf;
        }
        .parties-empty { padding: 3rem 1rem; text-align: center; color: #43474e; font-size: 0.875rem; }
        .parties-empty p { margin: 0; }

        .parties-avatar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: #d6e3ff;
          color: #001b3c;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 700;
          flex-shrink: 0;
        }
        .parties-fieldset {
          border: 1px solid #c4c6cf;
          border-radius: 4px;
          padding: 0.75rem 1rem 1rem;
          margin: 0 0 1rem;
          background: #f7f9fb;
        }
        .parties-fieldset legend {
          padding: 0 0.25rem;
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 10px;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #43474e;
        }
        .parties-contact-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
        }
        .parties-contact-row + .parties-contact-row { margin-top: 0.5rem; }
        .parties-contact-row .material-symbols-outlined {
          font-size: 16px;
          vertical-align: -3px;
          margin-right: 0.25rem;
        }
        .parties-contact-row button {
          background: none;
          border: none;
          padding: 0;
          color: #002045;
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 12px;
          cursor: pointer;
          white-space: nowrap;
        }
        .parties-contact-row button:hover { text-decoration: underline; }

        .modal-form { display: flex; flex-direction: column; gap: 1rem; }
        .form-group { display: flex; flex-direction: column; gap: 0.25rem; }
        .form-group label {
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 12px;
          color: #43474e;
        }
        .modal-actions { display: flex; gap: 0.5rem; justify-content: flex-end; margin-top: 0.5rem; }
        .error-message { color: #ba1a1a; font-size: 0.875rem; }
      `}</style>
    </div>
  );
}
